import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { format, parseISO } from "date-fns";
import { useState } from "react";
import { Button, Divider, Loading, Typography } from "@/components/atoms";
import { StepIndicator } from "@/components/molecules";
import { getPhpHostUrl } from "@/helpers/apiHelpers";
import { useCountryConfig, usePage, useProfile } from "@/hooks";
import { useAppDispatch } from "@/redux/hooks";
import { setPaymentMethodSetupId, setTakeManualPaymentStep } from "@/redux/slices/payment/paymentSlice";
import { CreditCardPaymentsItem } from "@/redux/slices/payment/types";
import { addToast, formatCurrency } from "@/utils";

interface TakeManualPaymentFormProps {
  payment: CreditCardPaymentsItem;
}

export const TakeManualPaymentForm = ({ payment }: TakeManualPaymentFormProps) => {
  const { currency, amount, dateTime, jobId } = payment;
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const { dateFormat } = useProfile();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { pathname } = usePage();
  const url = `${getPhpHostUrl()}/dashboard${pathname}`;
  const isStripeLoading = !stripe || !elements;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isStripeLoading) return;

    setIsSubmitting(true);
    const result = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: url },
      redirect: "if_required",
    });

    if (result.error) {
      addToast("danger", result.error.message || "Something went wrong. Please contact your administrator.");
    } else {
      dispatch(setPaymentMethodSetupId(result.setupIntent.id));
      dispatch(setTakeManualPaymentStep(2));
    }

    setIsSubmitting(false);
  };

  /** Follows the operator's country rather than assuming Australian BECS. */
  const { config } = useCountryConfig();
  const paymentMethodOrder = ["card", config.stripeDebitPaymentMethod].filter(Boolean) as string[];

  return (
    <form id="payment-form" onSubmit={handleSubmit} className="relative flex h-full w-full flex-col">
      {(isSubmitting || isStripeLoading) && <Loading />}
      <div className="h-full flex-1 overflow-y-auto p-5">
        <StepIndicator currentStep={1} numSteps={2} className="my-10 justify-center" />
        <div className="mb-8 flex flex-col">
          <Typography variant="h1">{`${currency} ${formatCurrency(amount, 2, currency)}`}</Typography>
          <Typography variant="title" className="text-neutral-dark-gray">{`Due ${format(parseISO(dateTime), dateFormat)}`}</Typography>
        </div>
        <div className="mb-8 flex flex-row">
          <Typography variant="action" className="flex-1 text-neutral-dark-gray">
            Job ID
          </Typography>
          <Typography variant="action" className="flex">
            {jobId}
          </Typography>
        </div>
        <Divider className="mb-2" />
        <PaymentElement
          options={{
            paymentMethodOrder: paymentMethodOrder,
            layout: {
              type: "tabs",
              defaultCollapsed: false,
            },
          }}
        />
      </div>
      <div>
        <Divider />
        <div className="flex flex-col gap-3 px-4 py-5">
          <Button type="submit" className="w-full text-center" size="lg">
            Proceed to Payment
          </Button>
        </div>
      </div>
    </form>
  );
};
